// Componentes de campo compartidos entre el ALTA (AddVinylView) y la EDICIÓN
// (EditVinylView) de un vinilo. Se extrajeron para reutilizar exactamente las
// mismas reglas de UI (bordes de error, desplegables de catálogo, cover picker,
// validación de obligatorios) en ambos flujos, garantizando consistencia.

import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { Icon } from '../ui/components.js';
import type {
  GenreOption,
  CustomFieldDef,
  CustomFieldScreen,
  RecordFieldConfig,
} from '../api/endpoints.js';

/**
 * Mapea la clave de campo ESTÁNDAR del backend (`RecordInput`) a la clave que
 * usan los formularios del front (que trabajan con NOMBRES para artista/sello/
 * género). Las claves no listadas se usan tal cual.
 */
export const STANDARD_FIELD_FRONT_KEY: Record<string, string> = {
  artist_id: 'artist',
  genre_id: 'genre_propio',
  label_id: 'label',
};

/**
 * Config de campos del tenant resuelta para una pantalla concreta (`add`/`edit`):
 * conjunto de claves de formulario obligatorias + definiciones de campos
 * personalizados que aplican a esa pantalla. La devuelve {@link useRecordFieldConfig}.
 */
export interface ResolvedFieldConfig {
  loading: boolean;
  /** Claves de formulario obligatorias (ya traducidas con STANDARD_FIELD_FRONT_KEY). */
  requiredFrontKeys: Set<string>;
  /** Campos personalizados a mostrar en esta pantalla. */
  customFields: CustomFieldDef[];
}

/**
 * Carga la configuración de campos del disco del tenant y la resuelve para la
 * pantalla indicada. Tolerante: ante un fallo, cae a `null` (sin obligatoriedad
 * extra ni campos personalizados), para no bloquear el formulario.
 */
export function useRecordFieldConfig(screen: CustomFieldScreen): ResolvedFieldConfig {
  const api = useApi();
  const [config, setConfig] = useState<RecordFieldConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.config
      .getRecordFields()
      .then((res) => {
        if (!cancelled) setConfig(res);
      })
      .catch(() => {
        if (!cancelled) setConfig(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  return useMemo<ResolvedFieldConfig>(() => {
    const requiredFrontKeys = new Set<string>(
      (config?.requiredStandard ?? []).map((k) => STANDARD_FIELD_FRONT_KEY[k] ?? k),
    );
    const customFields = (config?.customFields ?? []).filter(
      (f) => f.screens.length === 0 || f.screens.includes(screen),
    );
    return { loading, requiredFrontKeys, customFields };
  }, [config, loading, screen]);
}

/** `true` si un valor de campo personalizado está vacío (ausente/blanco). */
export function isCustomValueMissing(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

/**
 * Devuelve las claves de campos personalizados OBLIGATORIOS cuyo valor falta,
 * para resaltar y bloquear el guardado (Req. campos configurables).
 */
export function missingCustomKeys(
  defs: readonly CustomFieldDef[],
  values: Record<string, unknown>,
): string[] {
  return defs
    .filter((d) => d.required && isCustomValueMissing(values[d.key]))
    .map((d) => d.key);
}

/** Marca visual de campo obligatorio (*). */
export function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <label className="field-label">
      {label} {required && <span className="text-error">*</span>}
    </label>
  );
}

/** Sección del formulario con título, para agrupar campos legiblemente. */
export function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-space-sm">
      <p className="section-label text-vacuum-glow mb-space-xs">{title}</p>
      {children}
    </div>
  );
}

/** Clase del `field-slot` con borde rojo cuando el campo obligatorio falta. */
export function slotClass(invalid?: boolean): string {
  return `field-slot${invalid ? ' !border-error' : ''}`;
}

export function EditField({
  label,
  value,
  onChange,
  required,
  invalid,
}: {
  label: string;
  value: string | null;
  onChange: (v: string) => void;
  required?: boolean;
  invalid?: boolean;
}) {
  return (
    <div className="flex flex-col gap-space-xs">
      <FieldLabel label={label} required={required} />
      <div className={slotClass(invalid)}>
        <input className="field-input" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}

/** Desplegable de opciones fijas (tipo, tamaño). Soporta `required`. */
export function SelectField({
  label,
  value,
  options,
  onChange,
  required,
  invalid,
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (v: string | null) => void;
  required?: boolean;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-space-xs">
      <FieldLabel label={label} required={required} />
      <div className={slotClass(invalid)}>
        <select className="field-input" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">{t('fields.select')}</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/**
 * Campo combinado: desplegable de opciones conocidas + la opción "Otro…" que
 * revela un input para escribir un valor nuevo. Si el `value` actual no está
 * entre las opciones, se muestra igualmente seleccionado (no pierde lo actual).
 */
export function ComboField({
  label,
  value,
  options,
  placeholder,
  onChange,
  required,
  invalid,
}: {
  label: string;
  value: string | null;
  options: string[];
  placeholder?: string;
  onChange: (v: string | null) => void;
  required?: boolean;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  const [customMode, setCustomMode] = useState(false);
  const current = value ?? '';
  const inOptions = current !== '' && options.some((o) => o.toLowerCase() === current.toLowerCase());
  const showCustom = customMode || (current !== '' && !inOptions);

  return (
    <div className="flex flex-col gap-space-xs">
      <FieldLabel label={label} required={required} />
      {showCustom ? (
        <div className={`${slotClass(invalid)} pr-space-xs`}>
          <input
            className="field-input"
            value={current}
            placeholder={t('fields.writeValue')}
            autoFocus={customMode}
            onChange={(e) => onChange(e.target.value)}
          />
          {options.length > 0 && (
            <button
              type="button"
              aria-label={t('fields.chooseFromList')}
              className="text-on-surface-variant hover:text-primary transition-colors"
              onClick={() => {
                setCustomMode(false);
                onChange(null);
              }}
            >
              <Icon name="list" className="text-[18px]" />
            </button>
          )}
        </div>
      ) : (
        <div className={slotClass(invalid)}>
          <select
            className="field-input"
            value={inOptions ? current : ''}
            onChange={(e) => {
              if (e.target.value === '__custom__') {
                setCustomMode(true);
                onChange('');
              } else {
                onChange(e.target.value || null);
              }
            }}
          >
            <option value="">{placeholder ?? t('fields.select')}</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
            <option value="__custom__">{t('fields.other')}</option>
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * Género + subgénero dependientes: el género carga del catálogo (`propio`); el
 * subgénero depende del género elegido (catálogo `subgenre` + históricos). Ambos
 * permiten escribir un valor nuevo ("Otro…") para no bloquear taxonomías nuevas.
 */
export function GenreSubgenreFields({
  genre,
  subgenre,
  onGenreChange,
  onSubgenreChange,
  genreInvalid,
  subgenreInvalid,
  genreRequired = true,
  subgenreRequired = true,
}: {
  genre: string | null;
  subgenre: string | null;
  onGenreChange: (v: string) => void;
  onSubgenreChange: (v: string | null) => void;
  genreInvalid?: boolean;
  subgenreInvalid?: boolean;
  genreRequired?: boolean;
  subgenreRequired?: boolean;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [genreOptions, setGenreOptions] = useState<GenreOption[]>([]);
  const [subgenres, setSubgenres] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    api.genres
      .list('propio')
      .then((res) => {
        if (!cancelled) setGenreOptions(res.items);
      })
      .catch(() => {
        if (!cancelled) setGenreOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const genreId = useMemo(() => {
    const name = (genre ?? '').trim().toLowerCase();
    if (name === '') return null;
    return genreOptions.find((g) => g.name.toLowerCase() === name)?.id ?? null;
  }, [genreOptions, genre]);

  useEffect(() => {
    let cancelled = false;
    if (!genreId) {
      setSubgenres([]);
      return;
    }
    api.genres
      .subgenres(genreId)
      .then((r) => {
        if (!cancelled) setSubgenres(r.items);
      })
      .catch(() => {
        if (!cancelled) setSubgenres([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, genreId]);

  return (
    <>
      <ComboField
        label={t('fields.genre')} required={genreRequired}
        value={genre}
        options={genreOptions.map((g) => g.name)}
        placeholder={t('fields.selectGenre')}
        onChange={(v) => onGenreChange(v ?? '')}
        invalid={genreInvalid}
      />
      <ComboField
        label={t('fields.subgenre')} required={subgenreRequired}
        value={subgenre}
        options={subgenres}
        placeholder={
          (genre ?? '').trim() === '' ? t('fields.pickGenreFirst') : t('fields.selectSubgenre')
        }
        onChange={(v) => onSubgenreChange(v && v.trim() !== '' ? v : null)}
        invalid={subgenreInvalid}
      />
    </>
  );
}

/** Campo de tres estados Sí / No / (sin definir). */
export function YesNoField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-space-xs">
      <FieldLabel label={label} />
      <div className="field-slot">
        <select
          className="field-input"
          value={value == null ? '' : value ? 'yes' : 'no'}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'yes')}
        >
          <option value="">{t('fields.undefined')}</option>
          <option value="yes">{t('fields.yes')}</option>
          <option value="no">{t('fields.no')}</option>
        </select>
      </div>
    </div>
  );
}

/**
 * Campo de fecha con selector de calendario nativo (YYYY-MM-DD). Fuerza el
 * esquema oscuro y un ícono de calendario que abre el picker al tocar.
 */
export function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (v: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  function openPicker() {
    const el = inputRef.current;
    if (!el) return;
    const withPicker = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof withPicker.showPicker === 'function') {
      try {
        withPicker.showPicker();
        return;
      } catch {
        // Cae a focus si el navegador exige gesto directo.
      }
    }
    el.focus();
  }

  return (
    <div className="flex flex-col gap-space-xs">
      <FieldLabel label={label} />
      <div className="field-slot cursor-pointer" onClick={openPicker}>
        <input
          ref={inputRef}
          type="date"
          style={{ colorScheme: 'dark' }}
          className="field-input cursor-pointer"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
        <Icon name="calendar_today" className="text-on-surface-variant text-[18px] ml-space-xs" />
      </div>
    </div>
  );
}

/** Desplegable de condición Goldmine (fijo). */
export function ConditionSelect({
  label,
  value,
  onChange,
  required,
  invalid,
}: {
  label: string;
  value: string | null;
  onChange: (v: string) => void;
  required?: boolean;
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-space-xs">
      <FieldLabel label={label} required={required} />
      <div className={slotClass(invalid)}>
        <select className="field-input" value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('fields.select')}</option>
          {(['M', 'NM', 'VG+', 'VG', 'G+', 'G', 'P'] as const).map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/**
 * Selector de carátula: cuadrícula de miniaturas. Al tocar una, se marca como
 * carátula principal (la seleccionada se resalta con borde/checkmark cian).
 */
export function CoverPicker({
  images,
  selected,
  onSelect,
}: {
  images: string[];
  selected: string | null;
  onSelect: (url: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-4 gap-space-sm">
      {images.map((url, idx) => {
        const isSelected = url === selected || (selected == null && idx === 0);
        return (
          <button
            key={url}
            type="button"
            onClick={() => onSelect(url)}
            aria-label={t('fields.useImageAsCover', { n: idx + 1 })}
            aria-pressed={isSelected}
            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
              isSelected ? 'border-primary-container' : 'border-border-subtle hover:border-primary-container/50'
            }`}
          >
            <img src={url} alt={t('fields.coverOption', { n: idx + 1 })} className="w-full h-full object-cover" />
            {isSelected && (
              <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary-container text-surface-base flex items-center justify-center shadow">
                <Icon name="check" className="text-[14px]" />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function numStr(n: number | null | undefined): string {
  return n == null ? '' : String(n);
}
export function toNum(v: string): number | null {
  const n = Number(v);
  return v.trim() !== '' && Number.isFinite(n) ? n : null;
}

/**
 * Renderiza UN campo personalizado según su tipo (texto/número/sí-no/fecha/
 * lista) reutilizando los widgets existentes del formulario. El valor vive en el
 * mapa `custom_fields` del disco; `onChange` entrega el nuevo valor ya tipado.
 */
export function CustomFieldInput({
  def,
  value,
  onChange,
  invalid,
}: {
  def: CustomFieldDef;
  value: unknown;
  onChange: (v: unknown) => void;
  invalid?: boolean;
}) {
  switch (def.type) {
    case 'number':
      return (
        <EditField
          label={def.label}
          required={def.required}
          invalid={invalid}
          value={value == null ? '' : String(value)}
          onChange={(v) => onChange(v.trim() === '' ? null : toNum(v))}
        />
      );
    case 'boolean':
      return (
        <YesNoField
          label={def.label}
          value={typeof value === 'boolean' ? value : value === 'true' ? true : value === 'false' ? false : null}
          onChange={(v) => onChange(v)}
        />
      );
    case 'date':
      return (
        <DateField
          label={def.label}
          value={typeof value === 'string' ? value : null}
          onChange={(v) => onChange(v || null)}
        />
      );
    case 'select':
      return (
        <SelectField
          label={def.label}
          required={def.required}
          invalid={invalid}
          value={value == null ? null : String(value)}
          options={def.options ?? []}
          onChange={(v) => onChange(v)}
        />
      );
    case 'text':
    default:
      return (
        <EditField
          label={def.label}
          required={def.required}
          invalid={invalid}
          value={value == null ? '' : String(value)}
          onChange={(v) => onChange(v || null)}
        />
      );
  }
}

/**
 * Sección de CAMPOS PERSONALIZADOS del tenant. Si no hay ninguno para la
 * pantalla, no renderiza nada. Los valores se guardan en el mapa `values` y se
 * actualizan por clave con `onChange`. `invalidKeys` resalta los obligatorios
 * que faltan tras un intento de guardar.
 */
export function CustomFieldsSection({
  defs,
  values,
  onChange,
  invalidKeys,
}: {
  defs: readonly CustomFieldDef[];
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  invalidKeys?: Set<string>;
}) {
  const { t } = useTranslation();
  if (defs.length === 0) return null;
  return (
    <FormSection title={t('fields.customFields')}>
      <div className="grid grid-cols-1 gap-space-sm">
        {defs.map((def) => (
          <CustomFieldInput
            key={def.key}
            def={def}
            value={values[def.key]}
            onChange={(v) => onChange(def.key, v)}
            invalid={invalidKeys?.has(def.key)}
          />
        ))}
      </div>
    </FormSection>
  );
}
